import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getMajorVersion } from '../../helpers/VersionManager';

// Define the modal configuration interface
interface ModalConfig {
  id: string;
  localStorageKey: string;
  component: React.ComponentType;
}

// Define the modal order - add new modals here in the order they should appear
export const MODAL_ORDER: ModalConfig[] = [
  {
    id: 'disclaimer',
    localStorageKey: 'msi_api_disclaimer_acknowledged',
    component: () => null, // Will be replaced with actual components
  },
  {
    id: 'videoTutorial',
    localStorageKey: 'api_hub_video_tutorial_acknowledged',
    component: () => null, // Will be replaced with actual components
  },
  {
    id: 'releaseNotes',
    localStorageKey: `api_hub_release_notes_${getMajorVersion()}_acknowledged`,
    component: () => null, // Will be replaced with actual components
  },
  // Add more modals here as needed
];

interface ModalManagerContextType {
  currentModalId: string | null;
  acknowledgeModal: (modalId: string) => void;
  isModalAcknowledged: (modalId: string) => boolean;
  openModal: (modalId: string) => void;
}

const ModalManagerContext = createContext<ModalManagerContextType | undefined>(undefined);

export const useModalManager = () => {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error('useModalManager must be used within a ModalManagerProvider');
  }
  return context;
};

interface ModalManagerProviderProps {
  children: ReactNode;
}

export const ModalManagerProvider: React.FC<ModalManagerProviderProps> = ({ children }) => {
  const [currentModalId, setCurrentModalId] = useState<string | null>(null);

  const isModalAcknowledged = (modalId: string): boolean => {
    const modalConfig = MODAL_ORDER.find(modal => modal.id === modalId);
    if (!modalConfig) return true;
    return localStorage.getItem(modalConfig.localStorageKey) === 'true';
  };

  const openModal = (modalId: string) => {
    setCurrentModalId(modalId);
  };

  const acknowledgeModal = (modalId: string) => {
    const modalConfig = MODAL_ORDER.find(modal => modal.id === modalId);
    if (!modalConfig) return;

    localStorage.setItem(modalConfig.localStorageKey, 'true');
    
    // Find the next unacknowledged modal
    const currentIndex = MODAL_ORDER.findIndex(modal => modal.id === modalId);
    const nextModal = MODAL_ORDER.slice(currentIndex + 1).find(modal => !isModalAcknowledged(modal.id));
    
    setCurrentModalId(nextModal ? nextModal.id : null);
  };

  useEffect(() => {
    // Find the first unacknowledged modal on mount
    const firstUnacknowledgedModal = MODAL_ORDER.find(modal => !isModalAcknowledged(modal.id));
    if (firstUnacknowledgedModal) {
      setCurrentModalId(firstUnacknowledgedModal.id);
    }
  }, []);

  return (
    <ModalManagerContext.Provider
      value={{
        currentModalId,
        acknowledgeModal,
        isModalAcknowledged,
        openModal,
      }}
    >
      {children}
    </ModalManagerContext.Provider>
  );
};
